import { useState, useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Cell } from "recharts";
import { formatCurrency } from "@/utils/formatCurrency";
import { TrendingUp, BarChart3 } from "lucide-react";
import { ProfitTrendsData, DailyProfit, PeriodTab, ValueMode, periodConfig as sharedPeriodConfig, selectSeries } from "@trader-dashboard/shared";

export type { PeriodTab, ValueMode };
type ChartType = "line" | "bar";

type TrendsChartProps = {
  profitTrends: ProfitTrendsData;
  totalDeposits: number;
  period: PeriodTab;
  valueMode: ValueMode;
};

const formatDateLabel = (dateStr: string, period: PeriodTab): string => {
  const date = new Date(dateStr);
  if (period === "month" || period === "ytd") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  if (period === "year") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  // alltime uses monthly data
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

export const periodConfig = sharedPeriodConfig;

export const TrendsChart = ({ profitTrends, totalDeposits, period, valueMode }: TrendsChartProps): React.ReactNode => {
  const [chartType, setChartType] = useState<ChartType>("line");

  const rawData: DailyProfit[] = useMemo(
    () => selectSeries(profitTrends, period),
    [period, profitTrends]
  );

  const chartData = useMemo(() => {
    return rawData.map((d) => {
      const dollarValue = d.profit;
      const percentValue = totalDeposits > 0 ? (d.profit / totalDeposits) * 100 : 0;
      return {
        date: formatDateLabel(d.date, period),
        profit: valueMode === "$" ? dollarValue : Math.round(percentValue * 100) / 100,
      };
    });
  }, [rawData, valueMode, totalDeposits, period]);

  const totalProfit = rawData.reduce((sum, d) => sum + d.profit, 0);
  const chartColor = totalProfit >= 0 ? "#16a34a" : "#dc2626";
  const gradientId = `profitGradient-${period}-${valueMode}`;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>{periodConfig[period].title}</CardTitle>
            <CardDescription>
              Net profit per {period === "month" || period === "ytd" ? "day" : period === "year" ? "week" : "month"} (profit + commission + swap)
            </CardDescription>
          </div>
          <Tabs value={chartType} onValueChange={(v) => setChartType(v as ChartType)}>
            <TabsList className="h-8">
              <TabsTrigger value="line" className="px-2 py-1"><TrendingUp className="h-3.5 w-3.5" /></TabsTrigger>
              <TabsTrigger value="bar" className="px-2 py-1"><BarChart3 className="h-3.5 w-3.5" /></TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          {chartType === "line" ? (
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => valueMode === "$" ? `$${v}` : `${v}%`}
              />
              <ReferenceLine y={0} stroke="#d1d5db" strokeDasharray="3 3" />
              <Tooltip
                formatter={(value) => {
                  const num = Number(value);
                  return valueMode === "$" ? [formatCurrency(num), "Profit"] : [`${num.toFixed(2)}%`, "Profit"];
                }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
              />
              <Area
                type="monotone"
                dataKey="profit"
                stroke={chartColor}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
              />
            </AreaChart>
          ) : (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => valueMode === "$" ? `$${v}` : `${v}%`}
              />
              <ReferenceLine y={0} stroke="#d1d5db" strokeDasharray="3 3" />
              <Tooltip
                formatter={(value) => {
                  const num = Number(value);
                  return valueMode === "$" ? [formatCurrency(num), "Profit"] : [`${num.toFixed(2)}%`, "Profit"];
                }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
              />
              <Bar dataKey="profit" radius={[3, 3, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={entry.profit >= 0 ? "#16a34a" : "#dc2626"} />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
